// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { Instance as TippyInstance } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon/Icon";
import Typography from "@insite/mobius/Typography/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import HeaderClickable from "@plumberssupply/App/Components/Header/HeaderClickable";
import RecentlyPurchased, {
    HeaderRecentlyPurchasedWidgetId,
} from "@plumberssupply/App/Components/Header/RecentlyPurchased";
import { TippyPopupStyle } from "@plumberssupply/App/Components/Tippy/style";
import TippyPopup from "@plumberssupply/App/Components/Tippy/TippyPopup";
import useHeaderHeight from "@plumberssupply/App/Hooks/useHeaderHeight";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon, getXngHeader } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { getSettingsCollection } from "@plumberssupply/App/Store/Selectors/SettingsSelectors";
import IconCaretDown from "@plumberssupply/XDL/Icons/IconCaretDown";
import IconCaretUp from "@plumberssupply/XDL/Icons/IconCaretUp";
import IconPurchases from "@plumberssupply/XDL/Icons/IconPurchases";
import { color, xIsAuthenticated } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface RecentlyPurchasedLinkProps {}

const mapStateToProps = (state: CustomAppState) => ({
    canViewPurchasedProducts: xIsAuthenticated(state),
    products: getProductsForProductInfoList(state, HeaderRecentlyPurchasedWidgetId),
    isTabletScreen: getXngCommon(state).isTabletScreen,
    recentlyPurchasedUrl: getPageLinkByPageType(state, "RecentlyPurchasedPage")?.url,
    dropdownMenuHoverDelay: getSettingsCollection(state).customSettings.dropdownMenuHoverDelay,
});

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = RecentlyPurchasedLinkProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface RecentlyPurchasedLinkStyles {
    tippyPopup?: TippyPopupStyle;
}

const recentlyPurchasedLinkStyles: RecentlyPurchasedLinkStyles = {
    tippyPopup: {
        main: {
            css: css`
                &.tippy-box.tippy-popup {
                    & > .tippy-content {
                        padding-bottom: 0;
                    }
                    &&&[data-placement^="bottom"] > .tippy-arrow {
                        top: -18px;
                        // xng-change: had to use it to override tippy style
                        left: -42px !important;
                    }
                }
            `,
        },
    },
};

const styles = recentlyPurchasedLinkStyles;

const RecentlyPurchasedLink: FC<Props> = ({
    products,
    isTabletScreen,
    canViewPurchasedProducts,
    recentlyPurchasedUrl,
    loadPurchasedProducts,
    dropdownMenuHoverDelay,
}) => {
    const history = useHistory();
    const shellContext = useShellContext();
    const [isPopupShown, setIsPopupShown] = useState(false);
    const [isLoadPurchasedProductsLoading, setIsLoadPurchasedProductsLoading] = useState(false);

    useEffect(() => {
        if (canViewPurchasedProducts && (!products || products.length === 0)) {
            setIsLoadPurchasedProductsLoading(true);
            loadPurchasedProducts({
                widgetId: HeaderRecentlyPurchasedWidgetId,
                purchaseType: "recently",
                onComplete: () => {
                    setIsLoadPurchasedProductsLoading(false);
                },
            });
        }
    }, []);

    const openRecentlyPurchasedPageHandler = () => {
        if (!recentlyPurchasedUrl) {
            return;
        }
        history.push(recentlyPurchasedUrl);
    };

    const elementClickable = (
        <HeaderClickable
            isPopupShown={isPopupShown}
            iconProps={{ src: IconPurchases }}
            onClick={openRecentlyPurchasedPageHandler}
            data-test-selector="header_recently_purchased_link"
            aria-label="RecentlyPurchasedLink"
        >
            <>
                <StyledWrapper>
                    {translate("Recent")}
                    <Icon
                        src={isPopupShown ? IconCaretUp : IconCaretDown}
                        size={7}
                        color="common.ui8"
                        css={css`
                            margin-left: 5px;
                        `}
                    />
                </StyledWrapper>
                <Typography className="sub-title">{translate("Purchases")}</Typography>
            </>
        </HeaderClickable>
    );

    if (!canViewPurchasedProducts && !shellContext.isEditing) {
        return null;
    }

    if (isTabletScreen) {
        return elementClickable;
    }

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            content={
                <RecentlyPurchased
                    isLoading={isLoadPurchasedProductsLoading}
                    recentlyPurchasedPageUrl={recentlyPurchasedUrl}
                />
            }
            offset={[280, 17]}
            delay={[dropdownMenuHoverDelay || 120, 250]}
            arrow={true}
            maxWidth={1305}
            showCloseButton={false}
            onTriggerPopup={isOpen => {
                setIsPopupShown(isOpen);
            }}
            // appendTo={() => document?.body}
            placement="bottom-end"
            type="menu"
        >
            {elementClickable}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentlyPurchasedLink),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Recently Purchased Link",
        fieldDefinitions: [],
    },
};

export default widgetModule;
